/**
 * Cómo se escriben números, fechas y tiempos en pantalla (D-60): español de
 * México, `DD/MM/AAAA` en la interfaz, el dato viaja en ISO. Un solo lugar,
 * para que «hace 3 días» no se escriba de cuatro formas.
 */

const NUMERO = new Intl.NumberFormat("es-MX");

export function numero(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return NUMERO.format(n);
}

export function porcentaje(parte: number, total: number): string {
  if (!total) return "—";
  return `${Math.round((100 * parte) / total)} %`;
}

/** `2026-10-20` o `2026-10-20T15:04:05Z` → `20/10/2026`. */
export function fecha(iso: string | null | undefined): string {
  if (!iso) return "sin fecha";
  const [a, m, d] = iso.slice(0, 10).split("-");
  if (!a || !m || !d) return iso;
  return `${d}/${m}/${a}`;
}

/** `20/10/2026 15:04`, en la hora local del navegador. */
export function fechaHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  const dd = String(t.getDate()).padStart(2, "0");
  const mm = String(t.getMonth() + 1).padStart(2, "0");
  const hh = String(t.getHours()).padStart(2, "0");
  const mi = String(t.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${t.getFullYear()} ${hh}:${mi}`;
}

/**
 * «hace 5 min», «hace 3 h», «hace 2 días», «hace 3 semanas». Para la
 * actividad reciente y la última captura; para todo lo demás va la fecha.
 * `ahora` se inyecta para poder probarlo con reloj fijo.
 */
export function haceCuanto(iso: string | null | undefined, ahora: Date = new Date()): string {
  if (!iso) return "nunca";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const seg = Math.max(0, Math.round((ahora.getTime() - t) / 1000));
  if (seg < 60) return "hace un momento";
  const min = Math.round(seg / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 14) return `hace ${d} día${d === 1 ? "" : "s"}`;
  const sem = Math.round(d / 7);
  if (sem < 9) return `hace ${sem} semanas`;
  return `el ${fecha(iso)}`;
}

/** Cuenta en singular o plural sin inventar reglas: se dan las dos formas. */
export function plural(n: number, uno: string, varios: string): string {
  return `${numero(n)} ${n === 1 ? uno : varios}`;
}
